import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../data/constants';
import { getInventoryItemIdByPlant, getSubinventoryCodesByPlant } from '../../api/production-request-api/index.js';
import { useApiReady } from '@pella/fetch-client-react';

/**
 * Hook to fetch QOH Location Details given a valid item and orgID.
 * Returns a normalized shape: { data, isPending, error } where error is
 * a human-readable message string covering both API failures and soft
 * not-found cases (invalid item, no QOH locations).
 * @param {object} input
 * @param {number | undefined} input.organizationId
 * @param {string | undefined} input.item
 * @param {boolean} [input.enabled] - Optional override; when false the query is disabled regardless of other params
 */
const usePartQohLocationsLookup = ({ organizationId, item, enabled: enabledOverride = true }) => {
  const { apiReady } = useApiReady();
  const inventoryQuery = useQuery({
    queryKey: [QUERY_KEYS.INVENTORY_ITEM, organizationId, item],
    queryFn: () =>
      getInventoryItemIdByPlant({
        organizationId: /** @type {number} */ (organizationId),
        item: /** @type {string} */ (item),
      }),
    enabled: Boolean(enabledOverride && apiReady && organizationId && item),
  });

  const inventoryItemId = inventoryQuery.data?.[0]?.inventoryItemId;

  const qohQuery = useQuery({
    queryKey: [QUERY_KEYS.QOH_BY_ORG_AND_ITEM, organizationId, inventoryItemId],
    queryFn: () =>
      getSubinventoryCodesByPlant({
        organizationId: /** @type {number} */ (organizationId),
        inventoryItemId: /** @type {number} */ (inventoryItemId),
      }),
    enabled: Boolean(organizationId && inventoryItemId),
  });
  const error =
    (inventoryQuery.isError || (inventoryQuery.isSuccess && !inventoryQuery.data?.[0])
      ? 'Invalid item. No inventory record found for the entered item number.'
      : null) ||
    (qohQuery.isError || (qohQuery.isSuccess && !qohQuery.data?.length)
      ? 'No QOH locations found for the entered item number.'
      : null) ||
    null;

  return {
    data: qohQuery.data,
    isPending: inventoryQuery.isPending || qohQuery.isPending,
    error,
  };
};

export { usePartQohLocationsLookup };
